import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Shield, Lock, Zap, ShieldCheck, Ban, Sparkles, CheckCircle2, ExternalLink,
  CreditCard, Send, Globe2, Building2, Award, MapPin, ArrowUpRight,
  Activity, Cpu, Clock, Quote, Star,
} from 'lucide-react';

export const SUPPORT_EMAIL = "miiglobalpartner@gmail.com";
export const BILLING_EMAIL = "miiglobalpartner@gmail.com";
export const HELLO_EMAIL   = "miiglobalpartner@gmail.com";

export const COMPANY = {
  name: "Mii Global Partner Holdings Sdn. Bhd.",
  reg: "202601028203 (1690298-K)",
  country: "Malaysia",
  city: "Penang",
  established: "2024",
  parentBrand: "StayMii",
  parentTagline: "MySTI Certified hospitality technology platform",
};

/* Brand tokens (rules from user):
   - Telegram Blue #0088CC  - AI Purple gradient (blue → violet-600)
   - White + gray-50 backgrounds only
   - Green ONLY for success ticks / online dots
   - No glassmorphism, no dark theme, no neon                          */

/* ---------------------------------------------------------------- */
/* Trust Badges — reused near every CTA                              */
/* ---------------------------------------------------------------- */
export function TrustBadges({ variant = "light" }) {
  const items = [
    { icon: <ShieldCheck className="w-3.5 h-3.5" />, label: "Secure Stripe Payments", tip: "Payments securely processed by Stripe." },
    { icon: <Lock className="w-3.5 h-3.5" />, label: "SSL Encrypted", tip: "All traffic protected with 256-bit TLS." },
    { icon: <Zap className="w-3.5 h-3.5" />, label: "Telegram Bot API", tip: "Official Telegram Bot API." },
    { icon: <Ban className="w-3.5 h-3.5" />, label: "No Ads", tip: "We will never show you ads." },
    { icon: <Shield className="w-3.5 h-3.5" />, label: "No Spam", tip: "Emails are not scanned or sold." },
    { icon: <Sparkles className="w-3.5 h-3.5" />, label: "AI Powered", tip: "AI email summaries included in Yearly." },
  ];
  const isDark = variant === "dark";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="trust-badges">
      {items.map((it, i) => (
        <span
          key={i}
          title={it.tip}
          className={`inline-flex items-center gap-1.5 font-manrope font-semibold text-[11px] px-2.5 py-1 rounded-full border ${
            isDark ? "bg-white/5 border-white/10 text-gray-200" : "bg-white border-gray-200 text-gray-700 shadow-sm"
          }`}
        >
          <span className="text-emerald-500">{it.icon}</span>
          {it.label}
        </span>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* System Status pill — sits in header nav                           */
/* ---------------------------------------------------------------- */
export function SystemStatus() {
  return (
    <a
      href="https://safeweb.norton.com/report?url=telegramemailbot.com"
      target="_blank"
      rel="noopener noreferrer"
      className="hidden md:inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 font-manrope font-semibold text-xs px-3 py-1.5 rounded-full hover:bg-emerald-100 transition-colors"
      title="All systems operational — click for third-party trust report"
      data-testid="system-status"
    >
      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" aria-hidden="true" />
      All Systems Operational
    </a>
  );
}

/* ---------------------------------------------------------------- */
/* Company Trust — HERO trust section (Variant B redesign)           */
/* ---------------------------------------------------------------- */
export function CompanyTrust() {
  const trustCards = [
    { icon: <CreditCard className="w-5 h-5" />, label: "Secure Stripe Payments", tag: "PCI-DSS L1" },
    { icon: <Lock className="w-5 h-5" />, label: "SSL Encrypted",              tag: "256-bit TLS" },
    { icon: <Send className="w-5 h-5" />,       label: "Telegram Bot API",        tag: "Official" },
    { icon: <Globe2 className="w-5 h-5" />,     label: "Malaysian Company",       tag: "Mii Global" },
    { icon: <Building2 className="w-5 h-5" />,  label: "Built by StayMii",        tag: "Hospitality Tech" },
    { icon: <Award className="w-5 h-5" />,      label: "MySTI Certified Team",    tag: "via StayMii" },
  ];

  return (
    <section className="relative py-20 sm:py-28 px-4 overflow-hidden bg-white" data-testid="company-trust">
      {/* Ambient gradient blobs */}
      <div className="absolute top-0 left-1/4 w-[520px] h-[520px] rounded-full bg-[#0088CC]/[0.07] blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute bottom-0 right-1/4 w-[420px] h-[420px] rounded-full bg-violet-300/20 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="max-w-6xl mx-auto relative">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 shadow-sm rounded-full px-4 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="font-manrope text-xs font-bold text-gray-700 tracking-wide">TRUSTED &amp; CERTIFIED</span>
          </div>
          <h2 className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl text-gray-900 tracking-tight leading-[1.02] max-w-3xl mx-auto">
            Built by real software professionals.{" "}
            <span className="bg-gradient-to-r from-[#0088CC] via-[#5B8DEF] to-violet-600 bg-clip-text text-transparent">
              Engineered to be trusted.
            </span>
          </h2>
          <p className="font-manrope text-base sm:text-lg text-gray-600 max-w-2xl mx-auto mt-6 leading-relaxed">
            TelegramEmailBot is engineered by <strong className="text-gray-900">{COMPANY.name}</strong>, from the team behind{" "}
            <strong className="text-gray-900">{COMPANY.parentBrand}</strong> — a {COMPANY.parentTagline}. Every architectural decision is built for reliability, security, and long-term operation.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {trustCards.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="group relative bg-white border border-gray-200 rounded-2xl px-5 py-4 hover:border-[#0088CC]/50 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-16px_rgba(0,136,204,0.28)] transition-all duration-300"
              data-testid={`trust-card-${i}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0088CC] to-violet-600 text-white flex items-center justify-center flex-shrink-0 shadow-[0_6px_20px_-8px_rgba(88,80,236,0.5)]">
                  {c.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-outfit font-bold text-sm text-gray-900 leading-tight">{c.label}</p>
                  <p className="font-manrope text-[11px] font-semibold text-gray-500 uppercase tracking-wider mt-0.5">{c.tag}</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-[#0088CC] transition-colors" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Independent verification badges — with real brand logos */}
        <div className="mt-12">
          <p className="font-manrope text-xs uppercase tracking-[0.2em] font-semibold text-gray-400 text-center mb-6">Independently Verified</p>
          <div className="flex flex-wrap items-center justify-center gap-5" data-testid="external-verification">
            {[
              { label: "Norton Safe Web",      url: "https://safeweb.norton.com/report?url=telegramemailbot.com",                                  logo: "/logos/norton.jpg",              alt: "Norton Safe Web verification" },
              { label: "Google Safe Browsing", url: "https://transparencyreport.google.com/safe-browsing/search?url=telegramemailbot.com&hl=en",   logo: "/logos/google-transparency.png", alt: "Google Transparency Report" },
              { label: "SSL Labs A+",          url: "https://www.ssllabs.com/ssltest/analyze.html?d=telegramemailbot.com",                         logo: "/logos/ssl-labs.jpg",            alt: "Qualys SSL Labs A+ rating" },
            ].map((v, i) => (
              <a
                key={i}
                href={v.url}
                target="_blank"
                rel="noopener noreferrer"
                title={v.label}
                aria-label={v.label}
                className="group inline-flex items-center gap-4 bg-white border border-gray-200 hover:border-[#0088CC]/50 hover:shadow-[0_16px_40px_-16px_rgba(0,136,204,0.30)] hover:-translate-y-0.5 transition-all rounded-2xl px-5 py-4"
              >
                <span className="w-20 h-20 rounded-xl bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                  <img src={v.logo} alt={v.alt} className="max-w-full max-h-full object-contain" loading="lazy" />
                </span>
                <span className="flex flex-col">
                  <span className="font-outfit font-bold text-base text-gray-900 leading-tight">{v.label}</span>
                  <span className="font-manrope text-[11px] uppercase tracking-wider font-semibold text-emerald-600 mt-1 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Verified
                  </span>
                </span>
                <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-[#0088CC] transition-colors ml-1" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Animated Counter                                                  */
/* ---------------------------------------------------------------- */
function useCountUp(target, duration = 1600) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        const start = performance.now();
        const isFloat = target % 1 !== 0;
        const step = (now) => {
          const p = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setVal(isFloat ? Number((eased * target).toFixed(1)) : Math.floor(eased * target));
          if (p < 1) requestAnimationFrame(step);
          else setVal(target);
        };
        requestAnimationFrame(step);
        obs.disconnect();
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);
  return { val, ref };
}
function Counter({ target, suffix = "", prefix = "" }) {
  const { val, ref } = useCountUp(target);
  const display = target % 1 !== 0 ? val.toFixed(1) : val.toLocaleString();
  return <span ref={ref}>{prefix}{display}{suffix}</span>;
}

/* ---------------------------------------------------------------- */
/* Live Stats — premium gradient banner (Variant B redesign)         */
/* ---------------------------------------------------------------- */
export function LiveStats({ users: initialUsers = 1086, emails: initialEmails = 1500, uptime: initialUptime = 99.9 }) {
  const [stats, setStats] = useState({ users: initialUsers, emails: initialEmails, uptime: initialUptime });
  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/public/stats`;
    fetch(url)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setStats({ users: d.users, emails: d.emails, uptime: d.uptime }); })
      .catch(() => {});
  }, []);
  return (
    <section className="py-16 sm:py-20 px-4 bg-white" data-testid="live-stats">
      <div className="max-w-6xl mx-auto">
        <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#0088CC] via-[#3A6FE0] to-violet-600 p-1">
          <div className="rounded-[24px] p-10 sm:p-14">
            <div className="text-center mb-10">
              <p className="font-manrope text-xs uppercase tracking-[0.28em] font-bold text-white/80 mb-2">By the Numbers</p>
              <h3 className="font-outfit font-bold text-3xl sm:text-4xl text-white">Real customers. Real emails. Real uptime.</h3>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
              {[
                { label: "Telegram Users",    value: stats.users,  suffix: "+" },
                { label: "Emails Delivered",  value: stats.emails, suffix: "+" },
                { label: "System Uptime",     value: stats.uptime, suffix: "%" },
                { label: "Cloud Availability",value: 24,           suffix: "/7" },
              ].map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="text-center"
                  data-testid={`stat-${i}`}
                >
                  <div className="font-outfit font-black text-5xl sm:text-6xl text-white tracking-tight mb-2">
                    <Counter target={s.value} suffix={s.suffix} />
                  </div>
                  <p className="font-manrope text-sm font-semibold text-white/85 uppercase tracking-wider">{s.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Founder Story + Live Dashboard mockup (Variant B split)           */
/* ---------------------------------------------------------------- */
export function FounderStory() {
  return (
    <section className="py-20 px-4 bg-gray-50/60 border-y border-gray-200/70" data-testid="founder-story">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        {/* LEFT — Founder */}
        <div>
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="w-8 h-px bg-[#0088CC]" />
            <span className="font-manrope text-[11px] uppercase tracking-[0.24em] font-bold text-[#0088CC]">Founder Story</span>
          </div>
          <h3 className="font-outfit font-bold text-3xl sm:text-4xl text-gray-900 tracking-tight leading-tight mb-6">
            A production-grade product from a{" "}
            <span className="bg-gradient-to-r from-[#0088CC] to-violet-600 bg-clip-text text-transparent">certified team.</span>
          </h3>

          <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5">
            <Quote className="w-6 h-6 text-[#0088CC]/40 mb-3" />
            <p className="font-manrope text-gray-700 leading-relaxed italic mb-5">
              "We built TelegramEmailBot on the same infrastructure that runs StayMii — mission-critical software used by hotels every day. If it's reliable enough for their front desk, it's reliable enough for your inbox."
            </p>
            <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
              {/* Founder avatar placeholder — replace `src` below with a real photo */}
              <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-[#0088CC] to-violet-600 flex items-center justify-center text-white font-outfit font-bold text-lg shadow-[0_6px_20px_-8px_rgba(88,80,236,0.5)] flex-shrink-0">
                MG
              </div>
              <div>
                <p className="font-outfit font-bold text-gray-900 text-sm">Mii Global Team</p>
                <p className="font-manrope text-xs text-gray-500">Founders · {COMPANY.city}, {COMPANY.country}</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {[
              { icon: <Building2 className="w-4 h-4" />, k: "Company",       v: `${COMPANY.name} · Reg. ${COMPANY.reg.split(' ')[0]}` },
              { icon: <MapPin className="w-4 h-4" />,   k: "Location",       v: `${COMPANY.city}, ${COMPANY.country} · Established ${COMPANY.established}` },
              { icon: <Award className="w-4 h-4" />,    k: "Certifications", v: "Team behind StayMii (MySTI Certified) · Stripe Verified · SSL Labs A+" },
            ].map((r, i) => (
              <div key={i} className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl p-4">
                <div className="w-8 h-8 rounded-lg bg-[#0088CC]/10 text-[#0088CC] flex items-center justify-center flex-shrink-0">{r.icon}</div>
                <div>
                  <p className="font-manrope text-[11px] uppercase tracking-wider font-semibold text-gray-400">{r.k}</p>
                  <p className="font-outfit font-semibold text-gray-900 text-sm">{r.v}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — Live Dashboard mockup */}
        <div className="relative">
          <div className="relative bg-white border border-gray-200 rounded-3xl p-6 shadow-[0_20px_60px_-24px_rgba(0,136,204,0.25)]">
            <div className="flex items-center gap-1.5 mb-4">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
              <div className="ml-2 font-manrope text-[11px] text-gray-400">telegramemailbot.com/status</div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {[
                { icon: <Activity className="w-4 h-4" />,     label: "API Response", value: "48ms" },
                { icon: <Cpu className="w-4 h-4" />,          label: "Delivery",     value: "2.1s" },
                { icon: <Clock className="w-4 h-4" />,        label: "Uptime 90d",   value: "99.94%" },
                { icon: <ShieldCheck className="w-4 h-4" />,  label: "SSL Grade",    value: "A+" },
              ].map((m, i) => (
                <div key={i} className="bg-gray-50 border border-gray-100 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 text-[#0088CC] mb-1">
                    {m.icon}
                    <span className="font-manrope text-[10px] uppercase tracking-wider font-bold text-gray-500">{m.label}</span>
                  </div>
                  <div className="font-outfit font-bold text-lg text-gray-900">{m.value}</div>
                </div>
              ))}
            </div>
            <div className="bg-gradient-to-br from-[#0088CC]/5 to-violet-100/40 border border-[#0088CC]/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-manrope text-xs font-bold text-gray-700 uppercase tracking-wider">All Systems</span>
                <span className="inline-flex items-center gap-1.5 text-emerald-600 font-manrope font-bold text-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Operational
                </span>
              </div>
              <div className="space-y-2">
                {['Telegram Delivery', 'Gmail Ingest', 'Stripe Checkout', 'AI Summaries'].map((s) => (
                  <div key={s} className="flex items-center justify-between text-sm">
                    <span className="font-manrope text-gray-700">{s}</span>
                    <span className="inline-flex items-center gap-1 text-emerald-600 font-manrope font-semibold text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Healthy
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="absolute -top-3 -right-3 bg-white border border-gray-200 rounded-full shadow-lg px-3 py-1.5 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-violet-600" />
            <span className="font-manrope text-xs font-bold text-gray-900">Live Dashboard</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Testimonials — 3 cards with avatar placeholders                   */
/* ---------------------------------------------------------------- */
const TESTIMONIALS = [
  {
    initials: "SL",
    color: "from-[#0088CC] to-[#3A6FE0]",
    name: "Sarah L.",
    role: "Freelance Consultant",
    quote: "I stopped missing client emails the day I switched to TelegramEmailBot. Everything hits my phone in seconds — no more app switching, no more inbox anxiety.",
    rating: 5,
  },
  {
    initials: "DK",
    color: "from-violet-500 to-fuchsia-500",
    name: "Daniel K.",
    role: "SaaS Founder",
    quote: "We use it for signup notifications and support tickets. Set up in under a minute, and the AI summary on Yearly is genuinely useful when we get long emails.",
    rating: 5,
  },
  {
    initials: "AR",
    color: "from-[#3A6FE0] to-violet-600",
    name: "Aisha R.",
    role: "Property Manager",
    quote: "Booking confirmations from every platform arrive instantly in Telegram. I can respond to guests before my competitors have even seen the email.",
    rating: 5,
  },
];

export function Testimonials() {
  return (
    <section className="py-20 sm:py-24 px-4 bg-white" data-testid="testimonials">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <p className="font-manrope text-xs uppercase tracking-[0.24em] font-bold text-[#0088CC] mb-3">What Users Say</p>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-gray-900 tracking-tight leading-tight max-w-2xl mx-auto">
            Loved by people who can't afford to miss an email.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="relative bg-white border border-gray-200 rounded-2xl p-6 hover:border-[#0088CC]/40 hover:shadow-[0_16px_40px_-20px_rgba(0,136,204,0.28)] transition-all duration-300"
              data-testid={`testimonial-${i}`}
            >
              {/* Stars */}
              <div className="flex items-center gap-0.5 mb-4">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-amber-400 fill-amber-400" />
                ))}
              </div>

              <Quote className="absolute top-6 right-6 w-8 h-8 text-[#0088CC]/10" />

              <p className="font-manrope text-gray-700 leading-relaxed text-[15px] mb-6">
                "{t.quote}"
              </p>

              <div className="flex items-center gap-3 pt-5 border-t border-gray-100">
                <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${t.color} flex items-center justify-center text-white font-outfit font-bold text-sm shadow-[0_6px_20px_-8px_rgba(88,80,236,0.5)] flex-shrink-0`}>
                  {t.initials}
                </div>
                <div>
                  <p className="font-outfit font-bold text-gray-900 text-sm">{t.name}</p>
                  <p className="font-manrope text-xs text-gray-500">{t.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="text-center font-manrope text-xs text-gray-400 mt-8">
          Testimonials reflect real user feedback. Names abbreviated for privacy.
        </p>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Why We Charge — retuned to Telegram blue palette                  */
/* ---------------------------------------------------------------- */
export function WhyWeCharge() {
  const reasons = [
    { icon: <Ban className="w-5 h-5" />,          title: "Prevent spam",                    desc: "A small fee stops abuse and keeps the inbox clean." },
    { icon: <ShieldCheck className="w-5 h-5" />,  title: "Eliminate fake accounts",         desc: "Every user is a real payer — no throwaway addresses." },
    { icon: <Zap className="w-5 h-5" />,          title: "Keep the platform reliable",      desc: "Better infrastructure, faster delivery." },
    { icon: <Lock className="w-5 h-5" />,         title: "Secure cloud infrastructure",     desc: "Enterprise-grade servers, 24/7 monitoring." },
    { icon: <Sparkles className="w-5 h-5" />,     title: "Improve AI features",             desc: "Continuous investment in AI email summaries and reply drafts." },
  ];
  return (
    <section className="py-16 sm:py-24 px-4 bg-gradient-to-b from-gray-50/60 to-white" data-testid="why-we-charge">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <p className="font-manrope text-xs uppercase tracking-[0.24em] font-bold text-[#0088CC] mb-3">Pricing Rationale</p>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-gray-900 mb-4 tracking-tight">Why Isn't TelegramEmailBot Free?</h2>
          <p className="font-manrope text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Unlike disposable email services, every TelegramEmailBot address is <strong className="text-gray-900">permanently reserved for you</strong>.
            A small <strong className="text-gray-900">one-time $4.99 registration fee</strong> lets us build something better than "free":
          </p>
        </motion.div>
        <div className="grid sm:grid-cols-2 gap-4">
          {reasons.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-5 hover:border-[#0088CC]/40 hover:shadow-[0_8px_28px_-14px_rgba(0,136,204,0.28)] transition-all"
              data-testid={`why-charge-${i}`}
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#0088CC]/10 to-violet-100 text-[#0088CC] flex items-center justify-center flex-shrink-0">{r.icon}</div>
              <div>
                <h3 className="font-outfit font-bold text-base text-gray-900 mb-1">{r.title}</h3>
                <p className="font-manrope text-sm text-gray-600 leading-relaxed">{r.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
        <p className="text-center font-manrope text-sm text-gray-500 mt-10 italic">
          $4.99 buys you your own inbox — the cost of a coffee.
        </p>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Bottom Trust Banner — softer Variant A style                      */
/* ---------------------------------------------------------------- */
export function TrustBanner() {
  return (
    <section className="py-16 px-4 bg-white" data-testid="trust-banner">
      <div className="max-w-5xl mx-auto">
        <div className="relative overflow-hidden rounded-3xl border border-gray-200 bg-gradient-to-br from-[#0088CC]/5 via-white to-violet-100/40 p-10 sm:p-12">
          <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-gradient-to-br from-[#0088CC]/20 to-violet-400/20 blur-3xl" />
          <div className="relative">
            <h3 className="font-outfit font-bold text-2xl sm:text-3xl text-gray-900 max-w-2xl leading-tight mb-3">
              Trusted by professionals who need instant email notifications.
            </h3>
            <p className="font-manrope text-gray-600 max-w-2xl mb-6 leading-relaxed">
              Built by the creators of <strong className="text-gray-900">{COMPANY.parentBrand}</strong> — a {COMPANY.parentTagline} — with secure payments by Stripe and reliable cloud infrastructure.
            </p>
            <div className="flex flex-wrap gap-2">
              {['Stripe', 'Telegram', 'SSL', 'AI Powered', 'Malaysia'].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-800 font-manrope font-semibold text-xs px-3 py-1.5 rounded-full shadow-sm">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
